/**
 * Lesson Plan tab - LP generator cards extracted from Proposal Outline Builder.
 * Same logic, forms, and API calls - just in a dedicated tab.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useNavGuard } from '../contexts/NavGuardContext';
import ExcelPreviewCard from '../components/ExcelPreviewCard';

const DEFAULT_PLAN_ID = 'default_proposal_plan';

const dashboardTheme = {
  backgroundGradient: 'linear-gradient(135deg, #e1e6f7 0%, #cfd5ec 45%, #dfe4f5 100%)',
  cardBorder: '1px solid rgba(15, 23, 42, 0.08)',
  cardShadow: '0 35px 65px rgba(15, 23, 42, 0.08)',
  cardBg: 'rgba(255, 255, 255, 0.92)',
  insetBg: 'rgba(245, 246, 255, 0.85)',
  accentPrimary: '#5866f2',
  accentNavy: '#1f2a44',
  accentSlate: '#8b95b2',
  accentSuccess: '#2ab27b',
};

const pageWrapperStyle = {
  minHeight: '100vh',
  width: '100%',
  background: dashboardTheme.backgroundGradient,
  padding: '32px 36px 48px',
  boxSizing: 'border-box',
  margin: 0,
};

const cardBaseStyle = {
  borderRadius: 28,
  border: dashboardTheme.cardBorder,
  background: dashboardTheme.cardBg,
  boxShadow: dashboardTheme.cardShadow,
  backdropFilter: 'blur(18px)',
  padding: 24,
};

const inputSurfaceStyle = {
  borderRadius: 12,
  border: '1px solid rgba(15, 23, 42, 0.08)',
  background: 'rgba(255, 255, 255, 0.95)',
  padding: '10px 14px',
  fontSize: 13,
  boxShadow: '0 5px 20px rgba(15, 23, 42, 0.06)',
  transition: 'all 0.2s',
};

const ensureChildren = (node) => ({
  ...node,
  children: Array.isArray(node.children) ? node.children : [],
});

const flattenNodes = (nodes) => {
  const all = [];
  const walk = (items) => {
    (items || []).forEach((item) => {
      all.push(item);
      if (item.children && item.children.length) walk(item.children);
    });
  };
  walk(nodes);
  return all;
};

const LessonPlanPage = () => {
  const { planId: urlPlanId } = useParams();
  const navigate = useNavigate();
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  const decodedPlanId = urlPlanId ? decodeURIComponent(urlPlanId) : null;
  const getLastProposal = () => {
    try {
      return localStorage.getItem('propulse_last_worked_proposal') || null;
    } catch (e) {
      return null;
    }
  };
  const effectivePlanId = decodedPlanId || getLastProposal() || DEFAULT_PLAN_ID;

  // Redirect to correct URL if using localStorage planId
  useEffect(() => {
    if (!decodedPlanId && getLastProposal() && getLastProposal() !== DEFAULT_PLAN_ID) {
      navigate(`/lesson-plan/${encodeURIComponent(getLastProposal())}`, { replace: true });
    }
  }, [decodedPlanId, navigate]);

  const [connecting, setConnecting] = useState(false);

  // Proposal outline (for "Generate from proposal" card)
  const [proposalOutline, setProposalOutline] = useState([]);
  const [proposalOutlineLoading, setProposalOutlineLoading] = useState(false);
  const [selectedProposalNodeId, setSelectedProposalNodeId] = useState(null);

  // LP state
  const [lpOutline, setLpOutline] = useState([]);
  const [lpLoadingOutline, setLpLoadingOutline] = useState(false);
  const [lpSavingOutline, setLpSavingOutline] = useState(false);
  const [lpSelectedNodeId, setLpSelectedNodeId] = useState(null);
  const [lpNodeTitle, setLpNodeTitle] = useState('');
  const [lpNodeTimeMinutes, setLpNodeTimeMinutes] = useState(0);
  const [lpNodeRationale, setLpNodeRationale] = useState('');
  const [lpNodeMarkdown, setLpNodeMarkdown] = useState('');
  const [lpSavingNode, setLpSavingNode] = useState(false);
  const [lpGeneratingAll, setLpGeneratingAll] = useState(false);
  const [lpGeneratingNode, setLpGeneratingNode] = useState(false);
  const [lpStatus, setLpStatus] = useState('');
  const [excelRefreshKey, setExcelRefreshKey] = useState(0);

  // Unified LP form state
  const [unifiedLpRequirements, setUnifiedLpRequirements] = useState('');
  const [unifiedLpDocument, setUnifiedLpDocument] = useState('');
  const [unifiedLpDocumentFile, setUnifiedLpDocumentFile] = useState(null);
  const [unifiedLpDocumentFormat, setUnifiedLpDocumentFormat] = useState('');
  const [unifiedLpClientName, setUnifiedLpClientName] = useState('');
  const [unifiedLpTotalTime, setUnifiedLpTotalTime] = useState('');
  const [unifiedLpStructureHint, setUnifiedLpStructureHint] = useState('');
  const [unifiedLpGenerating, setUnifiedLpGenerating] = useState(false);
  const [unifiedLpStatus, setUnifiedLpStatus] = useState('');
  const [unifiedLpWorkshopFormat, setUnifiedLpWorkshopFormat] = useState('');
  const [unifiedLpCohortSize, setUnifiedLpCohortSize] = useState('');
  const [unifiedLpAverageAge, setUnifiedLpAverageAge] = useState('');
  const [unifiedLpGenderRatio, setUnifiedLpGenderRatio] = useState('');

  const { setLessonPlanCritical } = useNavGuard();
  useEffect(() => {
    setLessonPlanCritical(unifiedLpGenerating);
    return () => setLessonPlanCritical(false);
  }, [unifiedLpGenerating, setLessonPlanCritical]);

  const updateLpOutlineNode = (nodes, nodeId, updater) =>
    (nodes || []).map((node) => {
      if (node.id === nodeId) return ensureChildren(updater(node));
      if (node.children?.length)
        return ensureChildren({ ...node, children: updateLpOutlineNode(node.children, nodeId, updater) });
      return ensureChildren(node);
    });

  const removeLpOutlineNode = (nodes, nodeId) =>
    (nodes || [])
      .filter((n) => n.id !== nodeId)
      .map((n) =>
        n.children?.length
          ? ensureChildren({ ...n, children: removeLpOutlineNode(n.children, nodeId) })
          : ensureChildren(n)
      );

  const persistLpOutline = async (nextOutline) => {
    if (!effectivePlanId || effectivePlanId === DEFAULT_PLAN_ID) return;
    try {
      setLpSavingOutline(true);
      await apiService.saveLessonPlanOutline(effectivePlanId, nextOutline);
      setLpOutline(nextOutline.map(ensureChildren));
    } catch (e) {
      console.error('Failed to save LP outline', e);
      setLpStatus('Failed to save outline.');
      setTimeout(() => setLpStatus(''), 5000);
    } finally {
      setLpSavingOutline(false);
    }
  };

  const loadLpOutline = async (targetPlanId) => {
    if (!targetPlanId || targetPlanId === DEFAULT_PLAN_ID) return;
    try {
      setLpLoadingOutline(true);
      const data = await apiService.getLessonPlanOutline(targetPlanId);
      const next = Array.isArray(data?.outline) ? data.outline.map(ensureChildren) : [];
      setLpOutline(next);
      if (!lpSelectedNodeId && next.length > 0) {
        const first = next[0];
        setLpSelectedNodeId(first.id);
        const nodeData = await apiService.getLessonPlanNodeContent(targetPlanId, first.id);
        setLpNodeMarkdown(nodeData?.markdown || '');
      }
    } catch (e) {
      console.error('Failed to load LP outline', e);
      setLpOutline([]);
      setLpSelectedNodeId(null);
      setLpNodeTitle('');
      setLpNodeTimeMinutes(0);
      setLpNodeRationale('');
      setLpNodeMarkdown('');
    } finally {
      setLpLoadingOutline(false);
    }
  };

  const loadProposalOutline = async () => {
    if (!effectivePlanId || effectivePlanId === DEFAULT_PLAN_ID) return;
    try {
      setProposalOutlineLoading(true);
      const data = await apiService.getProposalOutline(effectivePlanId);
      const outline = Array.isArray(data?.outline) ? data.outline : [];
      setProposalOutline(outline);
      const flat = flattenNodes(outline);
      if (flat.length > 0 && !selectedProposalNodeId) {
        setSelectedProposalNodeId(flat[0].id);
      }
    } catch (e) {
      console.error('Failed to load proposal outline', e);
      setProposalOutline([]);
    } finally {
      setProposalOutlineLoading(false);
    }
  };

  const handleLpSelectNode = async (node) => {
    if (!effectivePlanId || !node?.id) return;
    setLpSelectedNodeId(node.id);
    setLpNodeTitle(node.title || '');
    setLpNodeTimeMinutes(node.time_minutes || 0);
    setLpNodeRationale(node.rationale || '');
    try {
      const data = await apiService.getLessonPlanNodeContent(effectivePlanId, node.id);
      setLpNodeMarkdown(data?.markdown || '');
    } catch (e) {
      setLpNodeMarkdown('');
    }
  };

  const handleLpNodeFieldChange = (updates) => {
    if (!lpSelectedNodeId) return;
    const next = updateLpOutlineNode(lpOutline, lpSelectedNodeId, (n) => ({ ...n, ...updates }));
    setLpOutline(next);
  };

  const handleLpAddRoot = async () => {
    const id = `lo_${Date.now()}`;
    const newNode = ensureChildren({
      id,
      title: 'New Learning Objective',
      type: 'lo',
      time_minutes: 90,
      rationale: '',
      children: [],
    });
    await persistLpOutline([...lpOutline, newNode]);
    await handleLpSelectNode(newNode);
  };

  const handleLpAddChild = async (parentId) => {
    const id = `topic_${Date.now()}`;
    const newChild = ensureChildren({
      id,
      title: 'New Topic',
      type: 'topic',
      time_minutes: 30,
      rationale: '',
      depends_on: [],
    });
    const next = updateLpOutlineNode(lpOutline, parentId, (n) => ({
      ...n,
      children: [...(n.children || []), newChild],
    }));
    await persistLpOutline(next);
    await handleLpSelectNode(newChild);
  };

  const handleLpDeleteNode = async (nodeId) => {
    if (!window.confirm('Delete this node and its children?')) return;
    const next = removeLpOutlineNode(lpOutline, nodeId);
    await persistLpOutline(next);
    if (lpSelectedNodeId === nodeId) {
      setLpSelectedNodeId(null);
      setLpNodeTitle('');
      setLpNodeTimeMinutes(0);
      setLpNodeRationale('');
      setLpNodeMarkdown('');
    }
  };

  const handleLpSaveNode = async () => {
    if (!effectivePlanId || !lpSelectedNodeId) return;
    try {
      setLpSavingNode(true);
      await apiService.saveLessonPlanNodeContent(effectivePlanId, lpSelectedNodeId, lpNodeMarkdown || '');
      setLpStatus('Lesson plan node saved.');
      setTimeout(() => setLpStatus(''), 4000);
    } catch (e) {
      setLpStatus('Failed to save lesson plan node.');
      setTimeout(() => setLpStatus(''), 5000);
    } finally {
      setLpSavingNode(false);
    }
  };

  const handleLpGenerateForAll = async () => {
    if (!effectivePlanId) return;
    try {
      setLpGeneratingAll(true);
      setLpStatus('Generating lesson plan chapters for all nodes...');
      const result = await apiService.generateLessonPlanFromProposal(effectivePlanId);
      const processed = result?.processed_nodes || [];
      setLpStatus(`Generated lesson plan chapters for ${processed.length} nodes.`);
      await loadLpOutline(effectivePlanId);
      setExcelRefreshKey((k) => k + 1);
      setTimeout(() => setLpStatus(''), 6000);
    } catch (e) {
      setLpStatus(e?.response?.data?.error || e?.message || 'Failed to generate.');
      setTimeout(() => setLpStatus(''), 8000);
    } finally {
      setLpGeneratingAll(false);
    }
  };

  const handleLpGenerateForNode = async () => {
    if (!effectivePlanId || !selectedProposalNodeId) return;
    try {
      setLpGeneratingNode(true);
      setLpStatus('Generating lesson plan chapter for selected node...');
      const result = await apiService.generateLessonPlanNodeFromProposal(effectivePlanId, selectedProposalNodeId);
      if (result?.markdown) setLpNodeMarkdown(result.markdown);
      await loadLpOutline(effectivePlanId);
      setLpStatus('Generated lesson plan chapter for selected node.');
      setExcelRefreshKey((k) => k + 1);
      setTimeout(() => setLpStatus(''), 6000);
    } catch (e) {
      setLpStatus(e?.response?.data?.error || e?.message || 'Failed.');
      setTimeout(() => setLpStatus(''), 8000);
    } finally {
      setLpGeneratingNode(false);
    }
  };

  const handleUnifiedLpGenerate = async () => {
    if (!effectivePlanId || effectivePlanId === DEFAULT_PLAN_ID) {
      setUnifiedLpStatus('Error: Select a proposal first. Open a proposal from Management.');
      setTimeout(() => setUnifiedLpStatus(''), 5000);
      return;
    }
    if (!unifiedLpRequirements && !unifiedLpDocument && !unifiedLpDocumentFile) {
      setUnifiedLpStatus('Error: Provide requirements, document, or both.');
      setTimeout(() => setUnifiedLpStatus(''), 5000);
      return;
    }

    let externalDocumentText = unifiedLpDocument || '';
    let documentFormat = unifiedLpDocumentFormat || '';

    if (unifiedLpDocumentFile) {
      try {
        setUnifiedLpGenerating(true);
        setUnifiedLpStatus('Parsing document...');
        const formData = new FormData();
        formData.append('document_file', unifiedLpDocumentFile);
        let fmt = documentFormat;
        if (!fmt && unifiedLpDocumentFile.name) {
          const n = unifiedLpDocumentFile.name.toLowerCase();
          if (n.endsWith('.pdf')) fmt = 'pdf';
          else if (n.endsWith('.docx') || n.endsWith('.doc')) fmt = 'docx';
          else if (n.endsWith('.md') || n.endsWith('.markdown')) fmt = 'markdown';
          else if (n.endsWith('.txt')) fmt = 'text';
        }
        if (fmt) formData.append('document_format', fmt);
        const parseResult = await apiService.parseLpDocument(formData);
        if (!parseResult?.success) {
          setUnifiedLpStatus(parseResult?.error || 'Failed to parse document.');
          setUnifiedLpGenerating(false);
          setTimeout(() => setUnifiedLpStatus(''), 5000);
          return;
        }
        externalDocumentText = parseResult.external_document || '';
        documentFormat = parseResult.document_format || fmt || 'text';
      } catch (e) {
        setUnifiedLpStatus(e?.response?.data?.error || e?.message || 'Failed to parse document.');
        setUnifiedLpGenerating(false);
        setTimeout(() => setUnifiedLpStatus(''), 5000);
        return;
      }
    }

    const wsBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/^https/, 'wss').replace(/^http/, 'ws').replace(/\/$/, '') || `wss://${window.location.host}`;
    const token = localStorage.getItem(process.env.REACT_APP_ACCESS_TOKEN_KEY || 'draftjam_access_token');
    const wsUrl = token ? `${wsBase}/ws/lp?token=${encodeURIComponent(token)}` : `${wsBase}/ws/lp`;

    const ensureWs = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) return true;
      if (wsRef.current?.readyState === WebSocket.CONNECTING) return false;
      try {
        setConnecting(true);
        const socket = new WebSocket(wsUrl);
        wsRef.current = socket;
        socket.onopen = () => setConnecting(false);
        socket.onclose = () => {
          setConnecting(false);
          if (!reconnectTimerRef.current) {
            reconnectTimerRef.current = setTimeout(() => {
              reconnectTimerRef.current = null;
              ensureWs();
            }, 1000);
          }
        };
        socket.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            if (!data?.type) return;
            if (data.type === 'lp_outline_update' && Array.isArray(data.outline)) {
              const next = data.outline.map(ensureChildren);
              setLpOutline(next);
              if (next.length > 0) {
                const latest = next[next.length - 1];
                setLpSelectedNodeId(latest.id);
                apiService.getLessonPlanNodeContent(effectivePlanId, latest.id).then((d) => setLpNodeMarkdown(d?.markdown || ''));
              }
            } else if (data.type === 'complete' && data.lesson_plan_id) {
              setUnifiedLpGenerating(false);
              if (data.outline) {
                const next = Array.isArray(data.outline) ? data.outline.map(ensureChildren) : [];
                setLpOutline(next);
                if (next.length > 0) {
                  const first = next[0];
                  setLpSelectedNodeId(first.id);
                  apiService.getLessonPlanNodeContent(effectivePlanId, first.id).then((d) => setLpNodeMarkdown(d?.markdown || ''));
                }
              }
              setUnifiedLpStatus(`Successfully generated LP outline with ${data.outline?.length || 0} Learning Objectives.`);
              setUnifiedLpRequirements('');
              setUnifiedLpDocument('');
              setUnifiedLpDocumentFile(null);
              setUnifiedLpDocumentFormat('');
              setUnifiedLpClientName('');
              setUnifiedLpTotalTime('');
              setUnifiedLpStructureHint('');
              setUnifiedLpWorkshopFormat('');
              setUnifiedLpCohortSize('');
              setUnifiedLpAverageAge('');
              setUnifiedLpGenderRatio('');
              setExcelRefreshKey((k) => k + 1);
            } else if (data.type === 'error') {
              setUnifiedLpGenerating(false);
              setUnifiedLpStatus(`Error: ${data.error || 'Generation failed'}`);
            }
          } catch (_) {}
        };
        socket.onerror = () => {};
      } catch (e) {
        setConnecting(false);
        return false;
      }
      return false;
    };

    if (!ensureWs()) {
      setUnifiedLpStatus('Error: WebSocket connection failed. Please refresh.');
      if (unifiedLpDocumentFile) setUnifiedLpGenerating(false);
      return;
    }
    try {
      setUnifiedLpGenerating(true);
      setUnifiedLpStatus('Generating LP outline...');
      wsRef.current.send(JSON.stringify({
        command: 'auto_generate_lp_unified',
        plan_id: effectivePlanId,
        requirements_prompt: unifiedLpRequirements || '',
        external_document: externalDocumentText,
        document_format: documentFormat || '',
        client_name: unifiedLpClientName || '',
        total_time_minutes: unifiedLpTotalTime ? parseInt(unifiedLpTotalTime) : undefined,
        structure_hint: unifiedLpStructureHint || '',
        workshop_context: {
          format: unifiedLpWorkshopFormat || undefined,
          cohortSize: unifiedLpCohortSize ? parseInt(unifiedLpCohortSize) : undefined,
          averageAge: unifiedLpAverageAge ? parseInt(unifiedLpAverageAge) : undefined,
          genderRatio: unifiedLpGenderRatio || undefined,
        },
        attached_context: {},
      }));
    } catch (e) {
      setUnifiedLpGenerating(false);
      setUnifiedLpStatus(`Error: ${e?.message || 'Failed'}`);
    }
  };

  useEffect(() => {
    if (effectivePlanId && effectivePlanId !== DEFAULT_PLAN_ID) {
      loadLpOutline(effectivePlanId);
      loadProposalOutline();
    } else {
      setLpOutline([]);
      setLpSelectedNodeId(null);
      setProposalOutline([]);
      setSelectedProposalNodeId(null);
    }
  }, [effectivePlanId]);

  useEffect(() => {
    const ws = wsRef.current;
    return () => {
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (ws) try { ws.close(); } catch (_) {}
    };
  }, []);

  const renderNodeForLp = (node, depth = 0) => {
    const isSelected = node.id === lpSelectedNodeId;
    return (
      <div key={node.id} style={{ marginLeft: depth * 14, marginBottom: 6 }}>
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            padding: depth === 0 ? '12px 14px' : '10px 12px',
            borderRadius: depth === 0 ? 16 : 12,
            border: isSelected ? '1px solid rgba(34, 197, 94, 0.5)' : '1px solid rgba(15, 23, 42, 0.08)',
            background: isSelected ? 'rgba(34, 197, 94, 0.08)' : 'rgba(255, 255, 255, 0.85)',
            cursor: 'pointer',
            fontSize: 12,
            color: dashboardTheme.accentNavy,
            minWidth: 0,
          }}
          onClick={() => handleLpSelectNode(node)}
        >
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={node.title || '(untitled)'}>{node.title || '(untitled)'}</span>
          <button onClick={(e) => { e.stopPropagation(); handleLpAddChild(node.id); }} style={{ flexShrink: 0, background: 'rgba(42, 178, 123, 0.18)', border: '1px solid rgba(42, 178, 123, 0.4)', color: '#1b7b56', borderRadius: 8, padding: '3px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>+</button>
          <button onClick={(e) => { e.stopPropagation(); handleLpDeleteNode(node.id); }} style={{ flexShrink: 0, background: 'rgba(255, 123, 106, 0.18)', border: '1px solid rgba(255, 123, 106, 0.4)', color: dashboardTheme.accentCoral, borderRadius: 8, padding: '3px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>×</button>
        </div>
        {node.children && node.children.length > 0 && (
          <div style={{ marginTop: 4 }}>{node.children.map((c) => renderNodeForLp(c, depth + 1))}</div>
        )}
      </div>
    );
  };

  const flatProposal = flattenNodes(proposalOutline);

  if (effectivePlanId === DEFAULT_PLAN_ID) {
    return (
      <div style={pageWrapperStyle}>
        <div style={{ ...cardBaseStyle, maxWidth: 600, margin: '40px auto', textAlign: 'center', padding: 48 }}>
          <h2 style={{ color: dashboardTheme.accentNavy, marginBottom: 16 }}>Lesson Plan Generator</h2>
          <p style={{ color: dashboardTheme.accentSlate, marginBottom: 24 }}>
            Select a proposal to work on. Open a proposal from the Proposal Outline tab first, then come back here.
          </p>
          <button
            onClick={() => navigate('/proposal-outline')}
            style={{ borderRadius: 12, border: 'none', background: dashboardTheme.accentPrimary, color: '#fff', padding: '12px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            Go to Proposal Outline
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={pageWrapperStyle}>
      <div style={{ maxWidth: 1440, margin: '0 auto', padding: '0 12px 60px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginBottom: 24, background: 'rgba(255, 255, 255, 0.92)', borderRadius: 32, padding: '22px 28px', boxShadow: '0 35px 70px rgba(15, 23, 42, 0.12)' }}>
          <div>
            <h2 style={{ fontSize: 28, fontWeight: 600, margin: 0, color: dashboardTheme.accentNavy }}>Lesson Plan Generator</h2>
            <p style={{ margin: '4px 0 0', fontSize: 14, color: dashboardTheme.accentSlate }}>Proposal: {effectivePlanId}</p>
          </div>
          <button onClick={() => navigate(`/proposal-outline/${encodeURIComponent(effectivePlanId)}`)} style={{ borderRadius: 12, border: '1px solid rgba(88, 102, 242, 0.5)', background: 'rgba(88, 102, 242, 0.1)', color: dashboardTheme.accentPrimary, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Open Proposal Outline
          </button>
        </div>

        {/* Generate LP Outline from Requirements */}
        <div style={{ ...cardBaseStyle, marginTop: 20 }}>
          <h3 style={{ marginTop: 0, fontSize: 18, color: dashboardTheme.accentNavy }}>Generate LP Outline from Requirements or Document</h3>
          <p style={{ fontSize: 12, color: dashboardTheme.accentSlate, marginTop: 4 }}>Generate a lesson plan outline directly from requirements or document. No proposal outline needed.</p>
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div><label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: dashboardTheme.accentNavy, marginBottom: 6 }}>Requirements (Optional)</label><textarea value={unifiedLpRequirements} onChange={(e) => setUnifiedLpRequirements(e.target.value)} placeholder="Enter learning requirements..." rows={4} style={{ ...inputSurfaceStyle, width: '100%', resize: 'vertical' }} /></div>
            <div><label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: dashboardTheme.accentNavy, marginBottom: 6 }}>External Document (Optional)</label><input type="file" accept=".pdf,.docx,.doc,.md,.markdown,.txt,.ppt,.pptx" onChange={(e) => { const f = e.target.files?.[0]; setUnifiedLpDocumentFile(f || null); if (f) { const n = f.name.toLowerCase(); if (n.endsWith('.pdf')) setUnifiedLpDocumentFormat('pdf'); else if (n.endsWith('.docx') || n.endsWith('.doc')) setUnifiedLpDocumentFormat('docx'); else if (n.endsWith('.md') || n.endsWith('.markdown')) setUnifiedLpDocumentFormat('markdown'); else if (n.endsWith('.txt')) setUnifiedLpDocumentFormat('text'); } }} style={{ fontSize: 13 }} /><span style={{ marginLeft: 8, fontSize: 12, color: dashboardTheme.accentSlate }}>or paste text below</span></div>
            <div><label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: dashboardTheme.accentNavy, marginBottom: 6 }}>Document Text (Optional)</label><textarea value={unifiedLpDocument} onChange={(e) => setUnifiedLpDocument(e.target.value)} placeholder="Paste document content..." rows={4} style={{ ...inputSurfaceStyle, width: '100%', resize: 'vertical', fontFamily: 'monospace' }} />{unifiedLpDocument && <div style={{ marginTop: 6 }}><label style={{ fontSize: 12, marginRight: 8 }}>Format:</label><select value={unifiedLpDocumentFormat} onChange={(e) => setUnifiedLpDocumentFormat(e.target.value)} style={{ ...inputSurfaceStyle }}><option value="">Auto</option><option value="pdf">PDF</option><option value="docx">DOCX</option><option value="markdown">Markdown</option><option value="text">Text</option></select></div>}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}><div><label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Client Name (Optional)</label><input type="text" value={unifiedLpClientName} onChange={(e) => setUnifiedLpClientName(e.target.value)} placeholder="Client/Organization name" style={{ ...inputSurfaceStyle, width: '100%' }} /></div><div><label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Total Time (Minutes, Optional)</label><input type="number" value={unifiedLpTotalTime} onChange={(e) => setUnifiedLpTotalTime(e.target.value)} placeholder="e.g., 480" style={{ ...inputSurfaceStyle, width: '100%' }} /></div></div>
            <div><label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Structure Hint (Optional)</label><input type="text" value={unifiedLpStructureHint} onChange={(e) => setUnifiedLpStructureHint(e.target.value)} placeholder="e.g., '5 modules, 3 days'" style={{ ...inputSurfaceStyle, width: '100%' }} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, borderTop: '1px solid rgba(15, 23, 42, 0.08)', paddingTop: 16, marginTop: 8 }}><div><label style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>Workshop Format</label><select value={unifiedLpWorkshopFormat} onChange={(e) => setUnifiedLpWorkshopFormat(e.target.value)} style={{ ...inputSurfaceStyle, width: '100%' }}><option value="">Select...</option><option value="online">Online</option><option value="offline">Offline</option><option value="hybrid">Hybrid</option></select></div><div><label style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>Cohort Size</label><input type="number" value={unifiedLpCohortSize} onChange={(e) => setUnifiedLpCohortSize(e.target.value)} placeholder="e.g., 25" style={{ ...inputSurfaceStyle, width: '100%' }} /></div><div><label style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>Average Age</label><input type="number" value={unifiedLpAverageAge} onChange={(e) => setUnifiedLpAverageAge(e.target.value)} placeholder="e.g., 32" style={{ ...inputSurfaceStyle, width: '100%' }} /></div><div><label style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>Gender Ratio</label><input value={unifiedLpGenderRatio} onChange={(e) => setUnifiedLpGenderRatio(e.target.value)} placeholder="e.g., 60% male, 40% female" style={{ ...inputSurfaceStyle, width: '100%' }} /></div></div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}><button onClick={handleUnifiedLpGenerate} disabled={!effectivePlanId || unifiedLpGenerating || (!unifiedLpRequirements && !unifiedLpDocument && !unifiedLpDocumentFile)} style={{ borderRadius: 16, border: '1px solid rgba(88, 102, 242, 0.7)', background: (!effectivePlanId || unifiedLpGenerating || (!unifiedLpRequirements && !unifiedLpDocument && !unifiedLpDocumentFile)) ? 'rgba(148, 163, 184, 0.4)' : 'rgba(88, 102, 242, 0.18)', color: dashboardTheme.accentNavy, padding: '12px 24px', fontSize: 14, fontWeight: 600, cursor: (!effectivePlanId || unifiedLpGenerating || (!unifiedLpRequirements && !unifiedLpDocument && !unifiedLpDocumentFile)) ? 'not-allowed' : 'pointer' }}>{unifiedLpGenerating ? 'Generating…' : 'Generate LP Outline'}</button>{unifiedLpStatus && <span style={{ fontSize: 12, color: unifiedLpStatus.includes('Error') ? '#dc2626' : dashboardTheme.accentSuccess }}>{unifiedLpStatus}</span>}</div>
          </div>
        </div>

        {/* Lesson Plan from Proposal - hidden */}
        {false && (
        <div style={{ ...cardBaseStyle, marginTop: 20 }}>
          <h3 style={{ marginTop: 0, fontSize: 18, color: dashboardTheme.accentNavy }}>Lesson Plan from this Proposal</h3>
          <p style={{ fontSize: 12, color: dashboardTheme.accentSlate, marginTop: 4 }}>Expand the proposal into facilitator-facing lesson plan chapters.</p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
            <button onClick={handleLpGenerateForNode} disabled={!effectivePlanId || !selectedProposalNodeId || lpGeneratingNode} style={{ borderRadius: 16, border: '1px solid rgba(88, 102, 242, 0.7)', background: (!effectivePlanId || !selectedProposalNodeId || lpGeneratingNode) ? 'rgba(148, 163, 184, 0.4)' : 'rgba(88, 102, 242, 0.18)', color: dashboardTheme.accentNavy, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: (!effectivePlanId || !selectedProposalNodeId || lpGeneratingNode) ? 'not-allowed' : 'pointer' }}>
              {lpGeneratingNode ? 'Generating…' : 'Generate LP for Selected Node'}
            </button>
            <button onClick={handleLpGenerateForAll} disabled={!effectivePlanId || !flatProposal.length || lpGeneratingAll} style={{ borderRadius: 16, border: '1px solid rgba(42, 178, 123, 0.7)', background: (!effectivePlanId || !flatProposal.length || lpGeneratingAll) ? 'rgba(156, 163, 175, 0.4)' : 'rgba(34, 197, 94, 0.16)', color: '#14532d', padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: (!effectivePlanId || !flatProposal.length || lpGeneratingAll) ? 'not-allowed' : 'pointer' }}>
              {lpGeneratingAll ? 'Generating…' : 'Generate LP for All Nodes'}
            </button>
            <button onClick={() => loadLpOutline(effectivePlanId)} disabled={!effectivePlanId || lpLoadingOutline} style={{ borderRadius: 16, border: '1px solid rgba(148, 163, 184, 0.8)', background: 'rgba(248, 250, 252, 0.9)', color: dashboardTheme.accentSlate, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: (!effectivePlanId || lpLoadingOutline) ? 'not-allowed' : 'pointer' }}>
              Refresh LP Outline
            </button>
            <button onClick={() => { apiService.clearLpOutlineAndExcel(effectivePlanId); loadLpOutline(effectivePlanId); setExcelRefreshKey((k) => k + 1); }} disabled={!effectivePlanId || lpLoadingOutline} style={{ borderRadius: 16, border: '1px solid rgba(239, 68, 68, 0.6)', background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: (!effectivePlanId || lpLoadingOutline) ? 'not-allowed' : 'pointer' }}>
              Delete LP Outline & Excel
            </button>
            {proposalOutlineLoading && <span style={{ fontSize: 12, color: dashboardTheme.accentSlate }}>Loading proposal…</span>}
            {!proposalOutlineLoading && flatProposal.length > 0 && (
              <select value={selectedProposalNodeId || ''} onChange={(e) => setSelectedProposalNodeId(e.target.value)} style={{ ...inputSurfaceStyle, maxWidth: 280 }}>
                <option value="">Select proposal node</option>
                {flatProposal.map((n) => (
                  <option key={n.id} value={n.id}>{n.title || n.id}</option>
                ))}
              </select>
            )}
          </div>
          {lpStatus && <div style={{ marginTop: 10, fontSize: 12, color: dashboardTheme.accentSlate }}>{lpStatus}</div>}
        </div>
        )}

        {/* LP Outline & Node Editor */}
        <div style={{ ...cardBaseStyle, marginTop: 20, display: 'flex', gap: 0, padding: 0, minHeight: 520 }}>
          <div
            style={{
              width: 320,
              minWidth: 320,
              maxWidth: 320,
              borderRight: '1px solid rgba(15, 23, 42, 0.08)',
              padding: 22,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              background: 'rgba(255, 255, 255, 0.9)',
              borderTopLeftRadius: 28,
              borderBottomLeftRadius: 28,
            }}
          >
            <h4 style={{ marginTop: 0, marginBottom: 8, fontSize: 14, color: dashboardTheme.accentNavy }}>Lesson Plan Outline</h4>
            <p style={{ fontSize: 11, color: dashboardTheme.accentSlate, marginBottom: 10 }}>Mirrors the proposal outline. Select a node to view or edit.</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              {lpOutline.length > 0 && <button onClick={handleLpAddRoot} disabled={lpSavingOutline || !effectivePlanId} style={{ borderRadius: 12, border: '1px solid #93c5fd', background: '#93c5fd', color: '#1e3a8a', padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: lpSavingOutline || !effectivePlanId ? 'not-allowed' : 'pointer', opacity: lpSavingOutline || !effectivePlanId ? 0.5 : 1 }}>+ Add LO</button>}
              <button onClick={() => { apiService.clearLpOutlineAndExcel(effectivePlanId); loadLpOutline(effectivePlanId); setExcelRefreshKey((k) => k + 1); }} disabled={!effectivePlanId || lpLoadingOutline} style={{ borderRadius: 16, border: '1px solid rgba(239, 68, 68, 0.6)', background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: (!effectivePlanId || lpLoadingOutline) ? 'not-allowed' : 'pointer' }}>Delete LP Outline & Excel</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingRight: 6, minHeight: 0, maxHeight: 500 }}>
              {lpLoadingOutline && <div style={{ fontSize: 12, color: dashboardTheme.accentSlate }}>Loading…</div>}
              {!lpLoadingOutline && (!lpOutline || lpOutline.length === 0) && <div style={{ fontSize: 12, color: dashboardTheme.accentSlate }}>Lesson plan outline will appear here after generation.</div>}
              {!lpLoadingOutline && lpOutline?.length > 0 && lpOutline.map((node) => renderNodeForLp(node))}
            </div>
          </div>
          <div style={{ flex: 1, padding: 22, borderTopRightRadius: 28, borderBottomRightRadius: 28, background: '#fff', minHeight: 260 }}>
            <h4 style={{ marginTop: 0, marginBottom: 8, fontSize: 14, color: dashboardTheme.accentNavy }}>Lesson Plan Node</h4>
            {lpSelectedNodeId ? (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div><label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: dashboardTheme.accentNavy, marginBottom: 4 }}>Title</label><input type="text" value={lpNodeTitle} onChange={(e) => { setLpNodeTitle(e.target.value); handleLpNodeFieldChange({ title: e.target.value }); }} style={{ ...inputSurfaceStyle, width: '100%' }} placeholder="Node title" /></div>
                  <div><label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: dashboardTheme.accentNavy, marginBottom: 4 }}>Time (minutes)</label><input type="number" value={lpNodeTimeMinutes} onChange={(e) => { const v = parseInt(e.target.value) || 0; setLpNodeTimeMinutes(v); handleLpNodeFieldChange({ time_minutes: v }); }} style={{ ...inputSurfaceStyle, width: '100%' }} placeholder="0" /></div>
                  <div><label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: dashboardTheme.accentNavy, marginBottom: 4 }}>Rationale</label><textarea value={lpNodeRationale} onChange={(e) => { setLpNodeRationale(e.target.value); handleLpNodeFieldChange({ rationale: e.target.value }); }} placeholder="Rationale…" rows={3} style={{ ...inputSurfaceStyle, width: '100%', resize: 'vertical' }} /></div>
                  <div><label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: dashboardTheme.accentNavy, marginBottom: 4 }}>Chapter Content (Markdown)</label><textarea value={lpNodeMarkdown} onChange={(e) => setLpNodeMarkdown(e.target.value)} placeholder="Lesson plan markdown…" rows={12} style={{ ...inputSurfaceStyle, width: '100%', minHeight: 220, resize: 'vertical', fontFamily: 'monospace' }} /></div>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                  <button onClick={handleLpSaveNode} disabled={lpSavingNode || !effectivePlanId} style={{ borderRadius: 16, border: '1px solid rgba(34, 197, 94, 0.7)', background: lpSavingNode ? 'rgba(74, 222, 128, 0.5)' : 'rgba(34, 197, 94, 0.16)', color: '#166534', padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: lpSavingNode || !effectivePlanId ? 'not-allowed' : 'pointer' }}>{lpSavingNode ? 'Saving…' : 'Save Content'}</button>
                  <button onClick={() => persistLpOutline(lpOutline)} disabled={lpSavingOutline || !effectivePlanId} style={{ borderRadius: 16, border: '1px solid rgba(59, 130, 246, 0.7)', background: lpSavingOutline ? 'rgba(147, 197, 253, 0.5)' : 'rgba(59, 130, 246, 0.16)', color: '#1e40af', padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: lpSavingOutline || !effectivePlanId ? 'not-allowed' : 'pointer' }}>{lpSavingOutline ? 'Saving…' : 'Save Outline'}</button>
                  <button onClick={() => handleLpDeleteNode(lpSelectedNodeId)} disabled={!effectivePlanId} style={{ borderRadius: 16, border: '1px solid rgba(239, 68, 68, 0.7)', background: 'rgba(239, 68, 68, 0.16)', color: '#dc2626', padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: !effectivePlanId ? 'not-allowed' : 'pointer' }}>Delete Node</button>
                </div>
              </>
            ) : (
              <p style={{ fontSize: 12, color: dashboardTheme.accentSlate }}>Select a node in the lesson plan outline to view or edit.</p>
            )}
          </div>
        </div>

        {/* Excel Slides Preview */}
        <div style={{ marginTop: 20 }}><ExcelPreviewCard planId={effectivePlanId} key={`excel-${effectivePlanId}-${excelRefreshKey}`} /></div>
      </div>
    </div>
  );
};

export default LessonPlanPage;
